import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { prop } from 'ramda';
import uuid from 'uuid-random';
import { useTranslations } from 'js/hooks/i18n';
import { useAppSelector } from 'js/store/hooks';
import { Icon } from 'js/components/common/Icon';
import { changeInputValue } from 'js/utils/DOM.util';
import { clsb, clsx } from 'js/utils/classNames.util';
import { componentService } from 'js/services/component.service';

import * as Types from './types';
import { Label } from '../Label';
import * as helpers from '../../helpers';
import { Suggestions } from './components';
import { parseSuggestions } from './helpers';
import { withFinalFormField } from '../../hocs';
import { FieldBottomMessage } from '../FieldBottomMessage';
import { ClearInputIcon } from './components/ClearInputIcon';

import '../../FormFields.scss';
import style from './Input.scss';

export const InputBare: React.FC<Types.IInputProps> = ({
    className,
    classNames,
    currencySymbol,
    disabled,
    hint,
    id,
    infoTooltip,
    isLabelAsteriskEnabled,
    isPlaceholderAsteriskEnabled,
    label,
    leftIcon,
    meta,
    onBlur,
    onChange,
    onFocus,
    onMouseDown,
    placeholder = '',
    readOnly,
    rightIcon,
    showPasswordEye,
    suggestions,
    type,
    withClearInputIcon = false,

    ...props
}) => {
    const idRef = useRef<string>(id || uuid());
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLSelectElement>(null);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const translate = useTranslations('playtech.mobile.system.input.');
    const isEnablePasswordSpy = useAppSelector(prop('isEnablePasswordSpy'));
    const isDisabled = meta?.submitting || meta?.validating || disabled;
    const showPasswordIcon = (showPasswordEye || isEnablePasswordSpy) && type === 'password';
    const [showClearInputIcon, setShowClearInputIcon] = useState<boolean>(Boolean(inputRef.current?.value));

    const interactionClass = helpers.getInteractionClass({
        readOnly,
        error: meta?.error,
        disabled: isDisabled,
        active: meta?.active,
        touched: meta?.touched,
        submitting: meta?.submitting
    });

    const togglePasswordVisible = useCallback(() => {
        setIsPasswordVisible(!isPasswordVisible);
    }, [setIsPasswordVisible, isPasswordVisible]);
    const ariaLabel = props['aria-label']
        ? `${currencySymbol || ''} ${props['aria-label']}`
        : translate('playtech.mobile.system.input.aria-label');

    const parsedSuggestions = useMemo(() => parseSuggestions(suggestions), [suggestions]);
    const hasSuggestions = Boolean(parsedSuggestions?.length) && !isDisabled && !readOnly;
    const onCombinedChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(event);
        setShowClearInputIcon(Boolean(event.target?.value));

        if (suggestionsRef.current) {
            suggestionsRef.current.value = event.target.value;
        }
    }, [onChange, setShowClearInputIcon]);

    const onSuggestionsChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        changeInputValue({ value: event.target.value, input: inputRef.current });
    }, []);

    const onClearInputIconClick = useCallback((): void => {
        changeInputValue({ value: '', input: inputRef.current });
        inputRef.current?.focus();
    }, []);

    return (
        <div
            onFocus={onFocus}
            onBlur={onBlur}
            className={clsx([clsb([style, classNames], ['container']), interactionClass, className])}
        >
            {(label || isLabelAsteriskEnabled) && (
                <Label
                    label={label}
                    id={idRef.current}
                    readOnly={readOnly}
                    classNames={classNames}
                    infoTooltip={infoTooltip}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                />
            )}
            <div className={clsb([style, classNames], hasSuggestions ? ['innerContainer', 'withSuggestions'] : ['innerContainer'])}>
                <div className={clsb([style, classNames], ['innerInputContainer'])}>
                    {leftIcon && leftIcon()}

                    {currencySymbol && <span className={clsb([style, classNames], ['currencySymbol'])}>{currencySymbol}</span>}

                    <input
                        {...props}
                        ref={inputRef}
                        id={idRef.current}
                        readOnly={readOnly}
                        disabled={isDisabled}
                        aria-label={ariaLabel}
                        onChange={onCombinedChange}
                        tabIndex={isDisabled || readOnly ? -1 : 0}
                        type={isPasswordVisible ? 'text' : type}
                        className={clsb([style, classNames], ['input'])}
                        onMouseDown={readOnly ? helpers.preventDefault : onMouseDown}
                        placeholder={`${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`}
                    />

                    {withClearInputIcon && showClearInputIcon && !isDisabled && !readOnly && (
                        <ClearInputIcon
                            onClick={onClearInputIconClick}
                            className={classNames?.clearInputIcon}
                        />
                    )}

                    {showPasswordIcon &&
                        (isPasswordVisible ? (
                            <Icon
                                type="passwordEyeShow"
                                onClick={togglePasswordVisible}
                                disabled={isDisabled || readOnly}
                                className={clsb([style, classNames], ['showPasswordIcon'])}
                                aria-label={translate('password.aria-label.hide')}
                            />
                        ) : (
                            <Icon
                                type="passwordEyeHide"
                                onClick={togglePasswordVisible}
                                disabled={isDisabled || readOnly}
                                className={clsb([style, classNames], ['showPasswordIcon'])}
                                aria-label={translate('password.aria-label.show')}
                            />
                        ))}

                    {rightIcon && rightIcon()}
                </div>
                {parsedSuggestions && hasSuggestions && (
                    <Suggestions
                        className={clsb([style, classNames], ['suggestions'])}
                        id={`${idRef.current}_suggestions`}
                        onChange={onSuggestionsChange}
                        suggestions={parsedSuggestions}
                        suggestionsRef={suggestionsRef}
                    />
                )}
            </div>

            <FieldBottomMessage hint={hint} meta={meta} classNames={classNames} />
        </div>
    );
};

export const InputPure = memo(InputBare);
export const Input = withFinalFormField<string>()(InputPure);

componentService.registerWebContentComponents({ Input: InputPure });
